import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Header } from "@/components/Header";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { GripVertical, Trash2, Eye, EyeOff, Pencil, Check, X } from "lucide-react";
import { CreateCategoryDialog } from "@/components/categories/CreateCategoryDialog";
import { 
  fetchCategories, 
  createCategory, 
  updateCategory, 
  toggleCategoryVisibility,
  updateCategoriesSequence,
  deleteCategory
} from "@/utils/categoryUtils";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { useIsMobile } from "@/hooks/use-mobile";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { useTranslation } from "react-i18next";

interface Category {
  id: string;
  name: string;
  is_visible: boolean;
  sequence: number;
  user_id: string;
  created_at: string;
}

const ServiceCategories = () => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [newCategoryName, setNewCategoryName] = useState("");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("visible");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<Category | null>(null);
  const [isReordering, setIsReordering] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState<string | null>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const { toast } = useToast();
  const isMobile = useIsMobile();
  const { t } = useTranslation();

  useEffect(() => {
    fetchCategoriesData();
  }, []);

  const fetchCategoriesData = async () => {
    const data = await fetchCategories();
    setCategories(data);
  };

  const handleCreateCategory = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const success = await createCategory(newCategoryName, user.id, categories);
    if (success) {
      setNewCategoryName("");
      setIsDialogOpen(false);
      fetchCategoriesData();
    }
  };

  const startEditing = (category: Category, e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    setEditingId(category.id);
    setEditingName(category.name);
  };

  const cancelEditing = (e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    setEditingId(null);
    setEditingName("");
  };

  const saveEditing = async (e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    if (!editingId || !editingName.trim()) return;

    const success = await updateCategory(editingId, editingName);
    if (success) {
      setEditingId(null);
      setEditingName("");
      fetchCategoriesData();
    }
  };

  const toggleVisibility = async (category: Category, e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    const success = await toggleCategoryVisibility(category);
    if (success) {
      fetchCategoriesData();
    }
  };

  const handleDeleteCategory = async () => {
    if (!categoryToDelete) return;
    
    const success = await deleteCategory(categoryToDelete);
    if (success) {
      fetchCategoriesData();
      setIsDeleteDialogOpen(false);
      setCategoryToDelete(null);
    }
  };

  const openDeleteDialog = (categoryId: string, e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    setCategoryToDelete(categoryId);
    setIsDeleteDialogOpen(true);
  };

  const handleDragEnd = async (result: any) => {
    if (!result.destination) return;
    
    const reorderedCategories = Array.from(filteredCategories);
    const [movedItem] = reorderedCategories.splice(result.source.index, 1);
    reorderedCategories.splice(result.destination.index, 0, movedItem);
    
    const updatedCategories = reorderedCategories.map((category, index) => ({
      ...category,
      sequence: index
    }));
    
    const newCategories = [...categories];
    updatedCategories.forEach(updatedCat => {
      const index = newCategories.findIndex(cat => cat.id === updatedCat.id);
      if (index !== -1) {
        newCategories[index] = updatedCat;
      }
    });
    setCategories(newCategories);
    
    const success = await updateCategoriesSequence(updatedCategories);
    if (!success) {
      fetchCategoriesData();
      toast({
        variant: "destructive",
        title: "Error",
        description: t('categories.sequenceUpdateError'),
      });
    } else {
      toast({
        title: "Success",
        description: t('categories.sequenceUpdateSuccess'),
      });
    }
  };

  const toggleReordering = () => {
    setIsReordering(!isReordering);
  };

  const filteredCategories = categories.filter(category => 
    activeTab === "visible" ? category.is_visible : !category.is_visible
  );

  const renderCategoryItem = (category: Category, index: number, isDragging: boolean = false) => {
    return (
      <div 
        key={category.id} 
        className={`flex items-center border-b last:border-b-0 hover:bg-gray-50 ${isDragging ? '' : 'p-3 sm:p-4'}`}
      >
        <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
          {isDragging && (
            <GripVertical className="h-5 w-5 text-gray-400 cursor-move flex-shrink-0" />
          )}
          
          {editingId === category.id ? (
            <div className="flex items-center gap-1 sm:gap-2 flex-1 min-w-0">
              <Input
                value={editingName}
                onChange={(e) => setEditingName(e.target.value)}
                className="max-w-[180px] sm:max-w-sm text-sm"
                autoFocus
                onClick={(e) => e.stopPropagation()}
              />
              <Button 
                size="sm" 
                variant="ghost" 
                onClick={saveEditing} 
                className="p-1 sm:p-2"
              >
                <Check className="h-4 w-4 text-green-600" />
              </Button>
              <Button 
                size="sm" 
                variant="ghost" 
                onClick={cancelEditing} 
                className="p-1 sm:p-2"
              >
                <X className="h-4 w-4 text-red-600" />
              </Button>
            </div>
          ) : (
            <span className="text-sm sm:text-base truncate flex-1 text-left">{category.name}</span>
          )}
        </div>
        
        {!isDragging && !editingId && (
          <div className="flex items-center gap-1 sm:gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={(e) => startEditing(category, e)}
              className="text-gray-500 hover:text-gray-700 hover:bg-gray-100 p-1.5 rounded-md"
            >
              <Pencil className="h-4 w-4" />
            </Button>
            
            <Button
              variant="ghost"
              size="sm"
              onClick={(e) => toggleVisibility(category, e)}
              className="text-gray-500 hover:text-gray-700 hover:bg-gray-100 p-1.5 rounded-md"
            >
              {category.is_visible ? 
                <EyeOff className="h-4 w-4" /> : 
                <Eye className="h-4 w-4" />
              }
            </Button>
            
            <Button 
              variant="ghost"
              size="sm"
              onClick={(e) => openDeleteDialog(category.id, e)}
              className="text-red-500 hover:text-red-700 hover:bg-red-50 p-1.5 rounded-md"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        )}
        
        {isDragging && (
          <div className="text-xs sm:text-sm text-gray-500 pl-2 flex-shrink-0">
            {t('categories.sequence')}: {category.sequence}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-white to-pink-50">
      <Header />
      <div className="container mx-auto px-2 sm:px-4 pt-16 sm:pt-20 pb-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
          <div className="flex items-center gap-2">
            {/* Info icon removed */}
          </div>
          <div className="flex flex-wrap items-center gap-2 sm:gap-4">
            <Button 
              variant="outline" 
              className={`border-gebeya-purple text-gebeya-purple hover:bg-gebeya-purple/10 ${isReordering ? 'bg-gebeya-purple/10' : ''} text-sm sm:text-base`}
              onClick={toggleReordering}
              size={isMobile ? "sm" : "default"}
            >
              {isReordering ? t('categories.doneReordering') : t('categories.changeSequence')}
            </Button>
            <CreateCategoryDialog
              isOpen={isDialogOpen}
              onOpenChange={setIsDialogOpen}
              categoryName={newCategoryName}
              onCategoryNameChange={setNewCategoryName}
              onCreateCategory={handleCreateCategory}
            />
          </div>
        </div>

        <Tabs 
          value={activeTab} 
          onValueChange={setActiveTab}
          className="space-y-4"
        >
          <TabsList className="bg-white border border-gebeya-purple/20 w-full sm:w-auto overflow-hidden">
            <TabsTrigger 
              value="visible"
              className="flex-1 data-[state=active]:bg-gradient-to-r data-[state=active]:from-gebeya-pink data-[state=active]:to-gebeya-orange data-[state=active]:text-white"
            >
              {t('categories.visible')}
            </TabsTrigger>
            <TabsTrigger 
              value="hidden"
              className="flex-1 data-[state=active]:bg-gradient-to-r data-[state=active]:from-gebeya-pink data-[state=active]:to-gebeya-orange data-[state=active]:text-white"
            >
              {t('categories.hidden')}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="visible" className="mt-6">
            <div className="bg-white rounded-lg shadow-lg border border-gebeya-purple/10 overflow-hidden">
              {isReordering ? (
                <DragDropContext onDragEnd={handleDragEnd}>
                  <Droppable droppableId="categories">
                    {(provided) => (
                      <div
                        {...provided.droppableProps}
                        ref={provided.innerRef}
                        className="overflow-x-auto"
                      >
                        {filteredCategories.map((category, index) => (
                          <Draggable key={category.id} draggableId={category.id} index={index}>
                            {(provided) => (
                              <div
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                                className="flex items-center justify-between p-3 sm:p-4 border-b last:border-b-0 hover:bg-gray-50 overflow-hidden"
                              >
                                {renderCategoryItem(category, index, true)}
                              </div>
                            )}
                          </Draggable>
                        ))}
                        {provided.placeholder}
                      </div>
                    )}
                  </Droppable>
                </DragDropContext>
              ) : (
                <div className="overflow-x-auto">
                  {filteredCategories.map((category, index) => 
                    renderCategoryItem(category, index)
                  )}
                  {filteredCategories.length === 0 && (
                    <div className="p-6 sm:p-8 text-center text-gebeya-purple/60">
                      {t('categories.noVisibleCategories')}
                    </div>
                  )}
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="hidden" className="mt-6">
            <div className="bg-white rounded-lg shadow-lg border border-gebeya-purple/10 overflow-hidden">
              {isReordering ? (
                <DragDropContext onDragEnd={handleDragEnd}>
                  <Droppable droppableId="hidden-categories">
                    {(provided) => (
                      <div
                        {...provided.droppableProps}
                        ref={provided.innerRef}
                        className="overflow-x-auto"
                      >
                        {filteredCategories.map((category, index) => (
                          <Draggable key={category.id} draggableId={category.id} index={index}>
                            {(provided) => (
                              <div
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                                className="flex items-center justify-between p-3 sm:p-4 border-b last:border-b-0 hover:bg-gray-50 overflow-hidden"
                              >
                                {renderCategoryItem(category, index, true)}
                              </div>
                            )}
                          </Draggable>
                        ))}
                        {provided.placeholder}
                      </div>
                    )}
                  </Droppable>
                </DragDropContext>
              ) : (
                <div className="overflow-x-auto">
                  {filteredCategories.map((category, index) => 
                    renderCategoryItem(category, index)
                  )}
                  {filteredCategories.length === 0 && (
                    <div className="p-6 sm:p-8 text-center text-gebeya-purple/60">
                      {t('categories.noHiddenCategories')}
                    </div>
                  )}
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>

      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('categories.deleteConfirmTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('categories.deleteConfirmDescription')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setCategoryToDelete(null)}>
              {t('common.cancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteCategory}
              className="bg-red-500 hover:bg-red-600 text-white"
            >
              {t('common.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ServiceCategories;
